// Phase SIKA-3 — N12 enforcement: bankroll exposure check.
//
// Source: SIKA_Independence_Architecture_v1.0 §11 N12 + §07 multi-goal
// portfolio guidance.
//
// Doctrine (N12 — advise, never block):
//   - Pure function. No DB calls, no state.
//   - Returns { total_pct, total, breach, breach_amount? } so the UI
//     can decide what to show. Never throws, never blocks a recommendation.
//   - When bankroll is null/undefined/0, returns honest { breach: false,
//     reason: 'no_bankroll_declared' } — N12 cannot fire without a
//     declared bankroll. This is the live-DB state on 2026-05-11
//     (user_bankrolls is empty for the entire user base).

export interface SikaActiveGoalForExposure {
  id: string;
  /** Current cycle's recommended weekly stake. null = no current cycle. */
  weekly_stake: number | null;
}

export interface BankrollExposureResult {
  total: number;
  total_pct: number;
  breach: boolean;
  breach_amount?: number;
  reason?: "no_bankroll_declared" | "no_active_goals" | "ok";
}

export const SIKA_N12_BANKROLL_CAP_PCT = 40;

/** Pure: compute combined-stake exposure across active goals. */
export function computeWeeklyExposure(
  goals: SikaActiveGoalForExposure[],
  bankroll: number | null | undefined,
): BankrollExposureResult {
  if (goals.length === 0) {
    return { total: 0, total_pct: 0, breach: false, reason: "no_active_goals" };
  }
  const total = goals.reduce((sum, g) => {
    const stake = typeof g.weekly_stake === "number" && g.weekly_stake > 0
      ? g.weekly_stake
      : 0;
    return sum + stake;
  }, 0);

  if (bankroll === null || bankroll === undefined || bankroll <= 0) {
    return {
      total,
      total_pct: 0,
      breach: false,
      reason: "no_bankroll_declared",
    };
  }

  const total_pct = (total / bankroll) * 100;
  const breach = total_pct > SIKA_N12_BANKROLL_CAP_PCT;
  const breach_amount = breach
    ? total - bankroll * (SIKA_N12_BANKROLL_CAP_PCT / 100)
    : undefined;

  return {
    total,
    total_pct: Math.round(total_pct * 10) / 10,
    breach,
    ...(breach_amount !== undefined ? { breach_amount: Math.round(breach_amount * 100) / 100 } : {}),
    reason: breach ? undefined : "ok",
  };
}

/** Human-readable advisory text. NEVER says "blocked" / "not allowed". */
export function formatExposureAdvisory(result: BankrollExposureResult): string {
  if (result.reason === "no_bankroll_declared") {
    return "Declare your bankroll to enable the 40% combined-stake advisory.";
  }
  if (result.reason === "no_active_goals") {
    return "No active goals.";
  }
  if (result.breach) {
    const pctTxt = String(result.total_pct);
    return `Combined weekly stake across active goals is ${pctTxt}% of bankroll — above the 40% advisory threshold. SIKA flags this; the decision is yours.`;
  }
  return `Combined weekly stake is ${String(result.total_pct)}% of bankroll — within the 40% advisory threshold.`;
}
